import isEmpty from 'lodash/isEmpty';
import words from 'lodash/words';
import without from 'lodash/without';

import { CampaignStateDefinition } from 'modules/graphql/types';
import { colors } from 'ui/theme';
import {
  ImageSegment,
  NODE_TYPES,
  RowSegment,
  SegmentArray,
  Segments,
  TextSegment,
  TextSegmentConfig,
  TextSegments,
  createLinkButtonSegment,
  createLinkSegment,
} from 'ui/workflows/flowchart';
import { aiIcon, quietHoursIcon, warningIcon } from 'ui/workflows/flowchart/icons';
import { CustomConversion } from 'modules/domains/campaign';
import {
  CAMEL_CASED_CLASS_NODE_TYPE_MAP,
  EVENT_TYPES,
  NODE_SECTION_TYPES,
  NODE_TYPES_IN_SECTION,
  STO_FLAG,
  TILE_METRICS_STUB,
} from '../../constants';
import {
  DataScienceOptimization,
  Metric,
  MetricDefinition,
  MetricDefinitionsBySendAction,
  MetricInformation,
  NodeType,
  SendNodeData,
} from '../../types';
import {
  addStubbedMetricsToSelectedMetrics,
  formatMetricNumber,
  getMetricTitle,
} from '../dataUtils/sendMetricsUtils';

export const NUMBER_OF_SEND_NODE_METRICS_TO_SHOW = 4;

const SEND_ACTION_NODES = NODE_TYPES_IN_SECTION[NODE_SECTION_TYPES.MESSAGES];
const spacer: TextSegment = { text: ' ' };

const getCampaignLinkSegment = (campaignId?: number): SegmentArray => {
  const isEnabled = Number.isInteger(campaignId);
  const url = isEnabled ? `/analytics/campaignPerformance?campaignId=${campaignId}` : null;
  return [createLinkSegment('View analytics', { url, isEnabled })];
};

const getMetricKey = (metric: MetricDefinition, isSet: boolean) => ({
  text: `${getMetricTitle(metric)}:  `,
  style: { fill: isSet ? colors.textSecondary : colors.textDisabled },
});

const getMetricValue = (metric: Metric, isSet = false) => ({
  text: formatMetricNumber(metric),
  style: { fill: isSet ? colors.textPrimary : colors.textDisabled },
});

const isCampaignArchived = (nodeData: SendNodeData) =>
  nodeData?.resource?.campaign?.campaignState === CampaignStateDefinition.Archived;

// REVIEW: create a campaign utils file?
const getIsSTOEnabled = (dataScienceOptimizations: DataScienceOptimization[] = []) =>
  dataScienceOptimizations.includes(STO_FLAG);

const getIcon = (
  args: Partial<Omit<ImageSegment, 'img'>> & Pick<ImageSegment, 'img'>,
): ImageSegment => ({
  width: 16,
  height: 16,
  isUnderlined: false,
  ...args,
});

const getArchivedWarningIcon = () =>
  getIcon({ img: warningIcon, tooltipText: 'This campaign has been archived' });

const getStoEnabledIcon = () =>
  getIcon({ img: aiIcon, tooltipText: 'This campaign has send time optimization enabled' });

const getQuietHoursEnabledIcon = () =>
  getIcon({ img: quietHoursIcon, tooltipText: 'This campaign has quiet hours enabled' });

const getCampaignSegment = (name?: string): TextSegment => ({ text: name || '' });

interface GetFooterSegmentsArgs {
  /** The type of the node. */
  nodeType: NodeType;
  /** An array of metric items. */
  metrics?: MetricInformation[];
  /** The campaign ID. */
  campaignId?: number;
  /** A flag indicating whether the `JourneyMetricSelection` feature flag is enabled. */
  isMetricsSelectionEnabled: boolean;
  /** The metrics that have been selected */
  selectedMetrics?: MetricDefinitionsBySendAction;
}

export const VIEW_METRICS_BUTTON_TYPE = 'view-metrics-button';

/**
 * Gets the footer segments for when the metrics are empty
 */
const getEmptyMetricsFooter = () => {
  const getRow = (segments: SegmentArray = []): RowSegment => ({
    isRow: true,
    style: { textAlign: 'center' },
    segments,
  });

  return [
    getRow([
      {
        text: 'No metrics selected',
        style: { fill: colors.textPrimary, fontWeight: 700 },
      },
    ]),
    getRow([{ text: 'To view relevant campaign insights,' }]),
    getRow([createLinkButtonSegment('select metrics', { buttonType: VIEW_METRICS_BUTTON_TYPE })]),
    [{ text: '' } as TextSegment],
  ];
};

/**
 * Gets an individual metrics row, which is a key-value pair
 */
const getMetricRow = (metric: Metric = {}) => {
  const { value } = metric || {};
  const isSet = typeof value !== 'undefined';
  return [getMetricKey(metric, isSet), getMetricValue(metric, isSet)];
};

/**
 * Consolidates the metrics and definitions into a single array of metrics.
 *
 * Unfortunately, the only thing in common is the metric nice name.  Definitely
 * wish the metric name was in there because it feels safer :/
 *
 * @param metrics The actual metrics, which include the values
 * @param definitions The metric definitions
 */
const consolidateMetrics = (
  metrics: MetricInformation[] = [],
  definitions: MetricDefinition[] = [],
): Metric[] => {
  const keyedInfo = metrics.reduce((acc: { [niceName: string]: MetricInformation }, metric) => {
    return { ...acc, [metric.displayName as string]: metric };
  }, {});

  return definitions.map((definition) => {
    return { ...definition, value: (keyedInfo[definition.metricNiceName as string] || {}).value };
  });
};

/**
 * Gets all the metric rows
 */
const getMetricRows = ({
  nodeType,
  metrics = [],
  isMetricsSelectionEnabled,
  selectedMetrics = {},
}: GetFooterSegmentsArgs): Segments => {
  const actionName = CAMEL_CASED_CLASS_NODE_TYPE_MAP[nodeType];
  const tileMetrics = isMetricsSelectionEnabled
    ? addStubbedMetricsToSelectedMetrics(selectedMetrics)
    : TILE_METRICS_STUB;
  const metricsDefinitions = tileMetrics[actionName];
  const consolidatedMetrics = consolidateMetrics(metrics, metricsDefinitions);

  if (isMetricsSelectionEnabled) {
    return metricsDefinitions?.length
      ? Array.from({ length: NUMBER_OF_SEND_NODE_METRICS_TO_SHOW }, (_, i) => {
          // if there is a metric type
          return consolidatedMetrics[i]
            ? // show the metric
              getMetricRow(consolidatedMetrics[i])
            : // otherwise, show a blank line
              [{ text: '' } as TextSegment];
        })
      : getEmptyMetricsFooter();
  }

  return consolidatedMetrics.map((metric) => getMetricRow(metric));
};

/**
 * Gets the footer segments for a send node
 */
const getSendNodeDescFooterSegments = (props: GetFooterSegmentsArgs): Segments => {
  const { campaignId, isMetricsSelectionEnabled } = props;
  const metricRows = getMetricRows(props);

  if (isMetricsSelectionEnabled) return metricRows;
  if (!metricRows.length) return [];
  // Add campaign button
  return [...metricRows, getCampaignLinkSegment(campaignId)];
};

const getDescriptionIcons = (nodeData: SendNodeData, isQuietHoursFeatureEnabled = false) => {
  const { dataScienceOptimizations, customQuietHours } = nodeData?.resource?.campaign || {};
  const icons: SegmentArray = [];

  if (getIsSTOEnabled(dataScienceOptimizations)) {
    icons.push(getStoEnabledIcon());
  }

  if (isQuietHoursFeatureEnabled && customQuietHours) {
    icons.push(getQuietHoursEnabledIcon());
  }

  return icons.reduce(
    (acc: SegmentArray, icon, i) => (i === 0 ? [icon] : [...acc, spacer, icon]),
    [],
  );
};

interface GetSendNodeDescBottom {
  nodeData: SendNodeData;
  nodeType: NODE_TYPES;
  isQuietHoursFeatureEnabled?: boolean;
  /** The project's timezone */
  projectTimeZone?: string;
}

const getSecondDescriptionRow = (args: GetSendNodeDescBottom) => {
  const { nodeData, nodeType, isQuietHoursFeatureEnabled = false, projectTimeZone } = args;

  if (nodeType !== NODE_TYPES.SEND_EMBEDDED) {
    return getDescriptionIcons(nodeData, isQuietHoursFeatureEnabled);
  }

  // If the campaign has not been created, don't show the display until text
  if (!nodeData?.campaignId) return [];

  const displayUntil = nodeData?.resource?.campaign?.displayUntil || {};
  const descriptions = [];

  if (displayUntil?.endDate) {
    const dateText = new Intl.DateTimeFormat(undefined, {
      timeZone: projectTimeZone,
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(new Date(displayUntil?.endDate));
    descriptions.push(
      // Remove commas from the date, as it is confusing with the rest of the
      // text being comma-separated
      dateText
        .split(',')
        .join(''),
    );
  }

  const query = displayUntil?.searchQuery?.searchQueries?.[0];

  if (query) {
    // TODO: Consolidate search query types
    // @ts-ignore
    const { field, value, comparatorType = '' } = query;
    // PascalCased comparators split into separate lowercase words
    // EG: 'IsSet' to 'is set'
    const comparator = words(comparatorType)
      .map((c) => c.toLocaleLowerCase())
      .join(' ');
    descriptions.push(`${field} ${comparator}${!isEmpty(value) ? ` ${value}` : ''}`);
  }

  const descriptionString = without([descriptions.join(', '), 'journey is off'], '').join(', or ');

  return [{ text: `Display until ${descriptionString}` }];
};

const getSendNodeDescBottomSegments = (args: GetSendNodeDescBottom) => {
  const { nodeData } = args;
  const { name } = nodeData?.resource?.campaign || {};
  const campaignIsArchived = isCampaignArchived(nodeData);
  const campaignSegment = getCampaignSegment(name);
  const firstRow = [
    // only add the archived icon if the campaign is archived
    ...(campaignIsArchived ? [getArchivedWarningIcon(), spacer] : []),
    campaignSegment,
  ];
  const secondRow = campaignIsArchived ? [] : getSecondDescriptionRow(args);

  return [firstRow, secondRow];
};

export const getSendNodeDescBottom = (args: GetSendNodeDescBottom): TextSegmentConfig => ({
  showTooltipIfTruncated: true,
  segments: getSendNodeDescBottomSegments(args),
});

export const getSendNodeDescFooter = (args: GetFooterSegmentsArgs): TextSegmentConfig => ({
  isTruncated: false,
  showTooltipIfTruncated: false,
  segments: getSendNodeDescFooterSegments(args),
});

export const sendNodeUtils = {
  getDescBottomSegments: getSendNodeDescBottom,
  getDescFooterSegments: getSendNodeDescFooter,
  getTextSegments: (
    nodeData: SendNodeData,
    nodeType: NODE_TYPES,
    {
      isQuietHoursFeatureEnabled = false,
      isMetricsSelectionEnabled = false,
      selectedMetrics,
      projectTimeZone,
    }: {
      isQuietHoursFeatureEnabled?: boolean;
      isMetricsSelectionEnabled?: boolean;
      selectedMetrics?: MetricDefinitionsBySendAction;
      projectTimeZone?: string;
    } = {},
  ): TextSegments => {
    const { textSegments = {} } = nodeData;
    const nodeDescBottom = getSendNodeDescBottom({
      nodeData,
      isQuietHoursFeatureEnabled,
      nodeType,
      projectTimeZone,
    });
    // Only calculate the footer segments if they don't already exist
    // This is because they'll only need calculation if it is a new node --
    // otherwise, they will already exist
    const nodeDescFooter = !isEmpty(textSegments.nodeDescFooter)
      ? textSegments.nodeDescFooter
      : getSendNodeDescFooter({ nodeType, isMetricsSelectionEnabled, selectedMetrics });
    return { ...textSegments, nodeDescBottom, nodeDescFooter };
  },
  getIsSTOEnabled,
  shouldUseCustomConversionMetric: (customConversion?: CustomConversion): boolean => {
    if (!customConversion) return false;

    const { eventName = EVENT_TYPES.PURCHASE } = customConversion;

    return eventName === EVENT_TYPES.PURCHASE;
  },
  isSendNode: (nodeType: string) => SEND_ACTION_NODES.includes(nodeType),
  nodeTypes: SEND_ACTION_NODES,
};

export default sendNodeUtils;
